import { useEffect } from 'react';
import PortalIdParser from 'PortalIdParser';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Confetti from 'growth-onboarding-confetti/Confetti';
import H4 from 'UIComponents/elements/headings/H4';
import { Text } from 'foundations-components/components/Text';
import { Tile } from 'foundations-components/transitional/components/Tile';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIModal from 'UIComponents/dialog/UIModal';
import UIButton from 'UIComponents/button/UIButton';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import { useTourTrackingMethod } from 'ui-shepherd-react/contexts/TourTrackingContext';
import { TourSource } from '../../util/tourSourceUtils';
import { getGrowthPlanUrl, getUserGuideUrl } from '../../constants/URL';
import { parseReturnUrl } from '../../util/urlUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const getFinishModalCtaBySource = tourSource => {
  const isFromGrowthPlan = tourSource === TourSource.GrowthPlan;
  return {
    modalCta: isFromGrowthPlan ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "onboarding-tours-modal.finish.backToPlan"
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "onboarding-tours-modal.finish.backToUserGuide"
    }),
    modalCtaLink: isFromGrowthPlan ? getGrowthPlanUrl() : getUserGuideUrl()
  };
};
const getFinishModalCtaContent = i18nPrefix => {
  if (isValidI18nKey(`onboarding-tours-${i18nPrefix}.finishModal.cta`)) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: `onboarding-tours-${i18nPrefix}.finishModal.cta`
    });
  }
};
const getFinishModalDefaultConfig = tourData => {
  const {
    i18nPrefix,
    returnUrl,
    tourSource
  } = tourData;
  const modalCtaBySource = getFinishModalCtaBySource(tourSource);
  const overrideCtaContent = getFinishModalCtaContent(i18nPrefix);
  return {
    modalCta: overrideCtaContent || modalCtaBySource.modalCta,
    modalCtaLink: returnUrl || modalCtaBySource.modalCtaLink
  };
};
const parseFinishModalCta = tourData => {
  const {
    tourCompleteModal
  } = tourData;
  const defaultModalConfig = getFinishModalDefaultConfig(tourData);
  if (!tourCompleteModal) {
    return defaultModalConfig;
  }
  const modalConfig = tourCompleteModal === true ? {} : tourCompleteModal;
  return {
    modalCta: modalConfig.cta || defaultModalConfig.modalCta,
    modalCtaLink: parseReturnUrl(modalConfig.ctaLink) || defaultModalConfig.modalCtaLink
  };
};
const OnboardingTourFinishModal = ({
  onClose,
  tourData,
  tourId,
  'data-test-step-index': dataTestStepIndex,
  rootNode
}) => {
  var _recommendNextTour$ct;
  const {
    recommendNextTour,
    i18nPrefix
  } = tourData;
  const {
    modalCta,
    modalCtaLink
  } = parseFinishModalCta(tourData);
  const trackTourViewFinishModal = useTourTrackingMethod('trackTourViewFinishModal');
  useEffect(() => {
    if (!trackTourViewFinishModal) {
      return;
    }
    trackTourViewFinishModal(tourId);
  }, [tourId, trackTourViewFinishModal]);
  return /*#__PURE__*/_jsxs(UIModal, {
    "data-test-step-index": dataTestStepIndex,
    rootNode: rootNode,
    use: "conversational",
    width: 550,
    children: [/*#__PURE__*/_jsx(Confetti, {}), /*#__PURE__*/_jsx(UIDialogHeader, {
      children: /*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: onClose
      })
    }), /*#__PURE__*/_jsxs(UIDialogBody, {
      children: [/*#__PURE__*/_jsx(H4, {
        children: isValidI18nKey(`onboarding-tours-${i18nPrefix}.finishModal.title`) ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: `onboarding-tours-${i18nPrefix}.finishModal.title`
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "onboarding-tours-modal.finish.title"
        })
      }), recommendNextTour && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Text, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "onboarding-tours-modal.finish.recommendTour"
          })
        }), /*#__PURE__*/_jsxs(Tile, {
          marginBlockStart: "300",
          marginInline: "600",
          paddingBlock: "600",
          paddingInline: "1000",
          children: [/*#__PURE__*/_jsx(Text, {
            as: "h5",
            textAlign: "start",
            children: recommendNextTour.title
          }), /*#__PURE__*/_jsx(Text, {
            as: "p",
            marginBlock: "300",
            textAlign: "start",
            children: recommendNextTour.text
          }), /*#__PURE__*/_jsx(ButtonWrapper, {
            children: /*#__PURE__*/_jsx(UIButton, {
              href: recommendNextTour === null || recommendNextTour === void 0 || (_recommendNextTour$ct = recommendNextTour.ctaLink) === null || _recommendNextTour$ct === void 0 ? void 0 : _recommendNextTour$ct.replace('%portalId%', String(PortalIdParser.get())),
              size: "sm",
              use: "primary",
              children: recommendNextTour.cta
            })
          })]
        })]
      })]
    }), /*#__PURE__*/_jsx(UIDialogFooter, {
      align: "center",
      children: /*#__PURE__*/_jsx(UIButton, {
        "data-test-id": "onboarding-tour-finish-modal-finish-button",
        href: modalCtaLink,
        size: recommendNextTour ? 'sm' : undefined,
        use: recommendNextTour ? undefined : 'primary',
        children: modalCta
      })
    })]
  });
};
export default OnboardingTourFinishModal;